/*
  GEMDRAW — Prize gallery

  Upgrades a list of photographs into a single-stage viewer. Everything it shows
  is already in the markup: the slides are rendered at full size and the
  thumbnails are anchors to them, so with the script blocked the section stays a
  working list of images that the browser can jump between.

  The two rules that shape the file:

  - It never creates a slide. If the markup has three images the viewer has
    three, and a gallery of one is left alone entirely — there is nothing to
    switch between.
  - It never traps a keyboard. The thumbnails stay anchors, so Tab reaches them
    and Enter activates them without any of this running; arrow keys are added
    on top as a convenience, not as the only way through.
*/

(function () {
  'use strict';

  class GdGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-gd-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gd-gallery-thumb]'));

      // One image is a picture, not a gallery. Leave it exactly as rendered.
      if (this.slides.length < 2 || this.thumbs.length < 2) return;

      this.setAttribute('data-gd-gallery-ready', '');
      this.index = 1;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (event) => {
          // The href still points at the slide, so a modifier click or a
          // middle click keeps its normal meaning.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          this.show(Number(thumb.getAttribute('data-gd-gallery-thumb')));
        });

        thumb.addEventListener('keydown', (event) => this.onKey(event));
      });

      this.show(1);
    }

    onKey(event) {
      const key = event.key;
      if (key !== 'ArrowRight' && key !== 'ArrowLeft') return;

      event.preventDefault();
      const step = key === 'ArrowRight' ? 1 : -1;
      const count = this.thumbs.length;

      // Wraps, because a gallery that stops at both ends makes the last image
      // feel like a dead end.
      const next = ((this.index - 1 + step + count) % count) + 1;
      this.show(next);
      const target = this.thumbs[next - 1];
      if (target) target.focus();
    }

    show(index) {
      this.index = index;

      this.slides.forEach((slide) => {
        const active = Number(slide.getAttribute('data-gd-gallery-slide')) === index;
        slide.classList.toggle('is-active', active);
        // Hidden from assistive technology as well as from view — a screen
        // reader should hear the image on the stage, not all of them.
        slide.toggleAttribute('hidden', !active);
      });

      this.thumbs.forEach((thumb) => {
        const active = Number(thumb.getAttribute('data-gd-gallery-thumb')) === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }
  }

  if (!customElements.get('gd-gallery')) {
    customElements.define('gd-gallery', GdGallery);
  }
})();
